import { FormikErrors } from "formik";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import { findQuestionById } from "src/services/formTemplate/v3";
import * as AF from "src/types/formTemplate";
import { z } from "zod";

export const SchoolSelectionItem: Item = {
  key: "0196b104-83a2-7370-8009-434e2280e499-SCHOOL-SELECTION", // just need any random unique key here
  label: "School selection",
};

export const NewRule: RuleType = {
  questionKey: "",
  optionKeys: [],
};

export const EmptyFormikValue: FormikType = {
  questionVisibilityJSON: "",
  questionVisibility: {
    switch: "disabled",
    logicalOperator: "and",
    rules: [NewRule],
  },
};

export const RuleSchema = z
  .object({
    questionKey: z
      .string()
      .min(1, "Choosing a question for the conditional reveal is required."),
    optionKeys: z.array(z.string()),
  })
  .superRefine((rule, ctx) => {
    if (rule.optionKeys.length < 1) {
      if (rule.questionKey === SchoolSelectionItem.key) {
        ctx.addIssue({
          code: "custom",
          path: ["optionKeys"],
          message: "At least one school is required",
        });
      } else {
        ctx.addIssue({
          code: "custom",
          path: ["optionKeys"],
          message: "At least one answer is required",
        });
      }
    }
  });

export const LastRuleSchema = z
  .object({
    questionKey: z.string().optional(), // Allows undefined
    optionKeys: z.array(z.string()), // Allows empty array
  })
  .superRefine((rule, ctx) => {
    if (rule.questionKey === undefined || rule.questionKey === "") {
      return;
    }

    if (rule.optionKeys.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["optionKeys"],
        message: "At least one answer is required",
      });
      return;
    }
  });

export type RuleType = z.infer<typeof RuleSchema>;
export type LastRuleType = z.infer<typeof LastRuleSchema>;

const LogicalOperatorSchema = z.union([z.literal("and"), z.literal("or")]);
export type LogicalOperator = z.infer<typeof LogicalOperatorSchema>;

export const FormikSchema = z.object({
  questionVisibilityJSON: z.string().optional(),
  questionVisibility: z.discriminatedUnion("switch", [
    z.object({
      switch: z.literal("disabled"),
      logicalOperator: z.any(),
      rules: z.array(z.any()),
    }),
    z.object({
      switch: z.literal("checked"),
      logicalOperator: LogicalOperatorSchema,

      // Special validation for rules array
      rules: z
        .array(z.union([RuleSchema, LastRuleSchema]))
        .superRefine((rules, ctx) => {
          // If there are no rules, it's valid
          if (rules.length === 0) {
            ctx.addIssue({
              code: "custom",
              message: "At least one rule is required",
              path: ["questionVisibility", "rules"],
            });
            return;
          }

          if (rules.length === 1) {
            const result = RuleSchema.safeParse(rules[0]);
            result.error?.issues.forEach((issue) =>
              ctx.addIssue({ ...issue, path: [0, ...issue.path] })
            );
            return;
          }

          const lastIndex = rules.length - 1;

          // For all rules except the last one, they must be valid according to RuleSchema
          for (let i = 0; i < lastIndex; i++) {
            const rule = rules[i];
            const result = RuleSchema.safeParse(rule);
            result.error?.issues.forEach((issue) =>
              ctx.addIssue({ ...issue, path: [i, ...issue.path] })
            );
          }

          // The last rule can either be a complete rule or an incomplete rule
          const lastRule = rules[lastIndex];
          const result = LastRuleSchema.safeParse(lastRule);
          result.error?.issues.forEach((issue) =>
            ctx.addIssue({ ...issue, path: [lastIndex, ...issue.path] })
          );
          return;
        }),
    }),
  ]),
});
export type FormikType = z.infer<typeof FormikSchema>;

export function toFormValue(
  sectionType: AF.V3.SectionType,
  question: AF.V3.Question
): FormikType {
  const visibility = question.visibility;
  if (visibility === undefined || visibility === null) {
    return EmptyFormikValue;
  }

  try {
    return {
      questionVisibility: {
        switch: "checked",
        logicalOperator: toFormLogicalOperator(visibility),
        rules: toFormRules(sectionType, visibility),
      },
    };
  } catch (error) {
    console.error(error);
    return EmptyFormikValue;
  }
}

function toFormRules(
  sectionType: AF.V3.SectionType,
  visibility: AF.V3.Visibility
): FormikType["questionVisibility"]["rules"] {
  const rules =
    (visibility && "allOf" in visibility ? visibility.allOf : undefined) ??
    (visibility && "anyOf" in visibility ? visibility.anyOf : undefined);
  if (rules === undefined) {
    throw new Error("Unsupported visibility configuration");
  }

  return rules.map((config) => {
    const rule = config.rules[0];
    if (rule === undefined) {
      throw new Error("Missing rule");
    }

    if (config.rules.length > 1) {
      throw new Error("Multiple rules are not supported");
    }

    switch (rule.dataType) {
      case "questionKey": {
        return {
          questionKey: rule.property,
          optionKeys: Array.isArray(rule.value) ? rule.value : [rule.value],
        };
      }
      case "school": {
        return {
          questionKey: SchoolSelectionItem.key,
          optionKeys: Array.isArray(rule.value) ? rule.value : [rule.value],
        };
      }
      default: {
        const _exhaustiveCheck: never = rule;
        throw new Error("Unhandled rule: " + rule);
      }
    }
  });
}

function toFormLogicalOperator(visibility: AF.V3.Visibility): LogicalOperator {
  if (visibility && "allOf" in visibility && visibility.allOf !== undefined) {
    return "and";
  }

  if (visibility && "anyOf" in visibility && visibility.anyOf !== undefined) {
    return "or";
  }

  throw new Error("Unsupported logical operator");
}

export function fromFormValue(formValue: FormikType): AF.V3.Visibility | null {
  if (formValue.questionVisibility.switch !== "checked") {
    return null;
  }

  const base = {
    default: "HIDDEN",
    result: "VISIBLE",
  } as const;

  switch (formValue.questionVisibility.logicalOperator) {
    case "and": {
      return {
        ...base,
        allOf: formValue.questionVisibility.rules.flatMap(toRule),
      };
    }
    case "or": {
      return {
        ...base,
        anyOf: formValue.questionVisibility.rules.flatMap(toRule),
      };
    }
    default: {
      const _exhaustiveCheck: never = formValue.questionVisibility;
      throw new Error(`Unhandled case for ${formValue.questionVisibility}`);
    }
  }
}

function toRule(rule: RuleType | LastRuleType): AF.V3.Rules[] {
  const result = RuleSchema.safeParse(rule);
  if (!result.success) {
    // ignore invalid rule
    return [];
  }

  if (result.data.questionKey === SchoolSelectionItem.key) {
    // School rule
    return [
      {
        rules: [
          {
            dataType: "school",
            operator: "IN",
            value: result.data.optionKeys,
          },
        ],
      },
    ];
  }

  // Question rule
  return [
    {
      rules: [
        {
          dataType: "questionKey",
          property: result.data.questionKey,
          operator: "IN",
          value: result.data.optionKeys,
        },
      ],
    },
  ];
}

export const INVALID_SCHOOL_SELECTION_KEY =
  "School selection is not available in this section type. Please select a question instead.";

export const INVALID_ANSWERS =
  "One or more selected answers are no longer available.";

export function validateForm(
  sectionType: AF.V3.SectionType,
  formTemplate: AF.V3.FormTemplate,
  values: FormikType
): FormikErrors<FormikType> {
  // FormikErrors type is annoying to work with since each field can be string | string[] | FormikErrors, so we need to jump through a lot of hoops to make it somewhat "type safe".
  // I don't think it's worth it, so I'm just going to use any for it. - Abadi
  let errors: any = {};
  if (values.questionVisibility.switch === "disabled") {
    return {};
  }

  // check if any of the rules is invalid
  values.questionVisibility.rules.forEach((rule, index) => {
    if (rule.questionKey === undefined) {
      return;
    }

    if (rule.questionKey === SchoolSelectionItem.key) {
      // this is school selection
      if (sectionType === "PreRankingSection") {
        errors = {
          ...errors,
          questionVisibility: {
            ...errors?.questionVisibility,
            rules: {
              ...errors?.questionVisibility?.rules,
              [`${index}`]: {
                ...errors?.questionVisibility?.rules[`${index}`],
                questionKey: INVALID_SCHOOL_SELECTION_KEY,
              },
            },
          },
        };
      }
    } else {
      // this is question rule
      // check if any of the selectedKey is invalid
      const question = findQuestionById(formTemplate, rule.questionKey);
      if (question) {
        const options = "options" in question ? question.options : [];

        // find invalid answer keys
        const isInvalid =
          rule.optionKeys.find(
            (key) => options.find((option) => option.id === key) === undefined
          ) !== undefined;

        if (isInvalid) {
          errors = {
            ...errors,
            questionVisibility: {
              ...errors?.questionVisibility,
              rules: {
                ...errors?.questionVisibility?.rules,
                [`${index}`]: {
                  ...errors?.questionVisibility?.rules[`${index}`],
                  optionKeys: INVALID_ANSWERS,
                },
              },
            },
          };
        }
      }
    }
  });

  return errors;
}
